import React, { useCallback, useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { sitesApi, type Site, type SiteArea } from '../../services/sitesApi';

const COMMON_TIMEZONES = [
  'America/Toronto',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Vancouver',
  'UTC',
  'Europe/London',
  'Europe/Berlin',
  'Asia/Singapore',
  'Asia/Tokyo',
  'Australia/Sydney',
];

function apiErrorMessage(err: unknown): string {
  const d = (err as { response?: { data?: Record<string, unknown> } })?.response?.data;
  if (d?.error === 'area_referenced') {
    const detail = typeof d.detail === 'string' ? d.detail : 'Area is still referenced';
    return detail;
  }
  if (typeof d?.detail === 'string') return d.detail;
  if (typeof d?.error === 'string') return d.error;
  if (err instanceof Error && err.message) return err.message;
  return 'Request failed';
}

type Props = {
  orgId: string;
  siteId: string;
  canWrite: boolean;
};

export const SiteLocationSettingsPanel: React.FC<Props> = ({ orgId, siteId, canWrite }) => {
  const [site, setSite] = useState<Site | null>(null);
  const [areas, setAreas] = useState<SiteArea[]>([]);
  const [displayName, setDisplayName] = useState('');
  const [timezone, setTimezone] = useState('');
  const [newAreaId, setNewAreaId] = useState('');
  const [newAreaName, setNewAreaName] = useState('');
  const [renameDrafts, setRenameDrafts] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const scopedSite = String(siteId || '').trim();
  const scopedOrg = String(orgId || '').trim();

  const reload = useCallback(async () => {
    if (!scopedSite) return;
    setError(null);
    const [siteRow, areaRows] = await Promise.all([
      sitesApi.getSite(scopedSite),
      sitesApi.listAreas(scopedSite),
    ]);
    setSite(siteRow);
    setDisplayName(String(siteRow.display_name || '').trim());
    setTimezone(String(siteRow.timezone || '').trim());
    setAreas(Array.isArray(areaRows) ? areaRows : []);
    const drafts: Record<string, string> = {};
    for (const area of areaRows || []) {
      drafts[area.area_id] = String(area.display_name || area.area_id);
    }
    setRenameDrafts(drafts);
  }, [scopedSite]);

  useEffect(() => {
    if (!scopedSite) return;
    reload().catch((err) => setError(apiErrorMessage(err)));
  }, [scopedSite, reload]);

  const saveSiteMeta = async () => {
    if (!scopedSite || !canWrite) return;
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      const updated = await sitesApi.updateSite(scopedSite, {
        display_name: displayName.trim() || scopedSite,
        timezone: timezone.trim() || null,
      });
      setSite(updated);
      setInfo('Site metadata saved');
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const createArea = async () => {
    if (!scopedSite || !canWrite) return;
    const areaId = newAreaId.trim();
    if (!areaId) {
      setError('area_id is required');
      return;
    }
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      await sitesApi.createArea(scopedSite, {
        area_id: areaId,
        display_name: newAreaName.trim() || areaId,
      });
      setNewAreaId('');
      setNewAreaName('');
      await reload();
      setInfo(`Area ${areaId} created`);
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const renameArea = async (areaId: string) => {
    if (!scopedSite || !canWrite) return;
    const name = String(renameDrafts[areaId] || '').trim();
    if (!name) return;
    setBusy(true);
    setError(null);
    try {
      await sitesApi.updateArea(scopedSite, areaId, { display_name: name });
      await reload();
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const setAreaActive = async (areaId: string, active: boolean) => {
    if (!scopedSite || !canWrite) return;
    setBusy(true);
    setError(null);
    try {
      await sitesApi.updateArea(scopedSite, areaId, { active });
      await reload();
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const removeArea = async (areaId: string) => {
    if (!scopedSite || !canWrite) return;
    setBusy(true);
    setError(null);
    try {
      await sitesApi.deleteArea(scopedSite, areaId);
      await reload();
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (!scopedSite) {
    return (
      <Alert severity="info">Set org/site scope to manage site location metadata.</Alert>
    );
  }

  return (
    <Accordion defaultExpanded={false} disableGutters>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Site location settings
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {site?.display_name || scopedSite}
            {scopedOrg ? ` · ${scopedOrg}` : ''}
            {timezone ? ` · ${timezone}` : ''}
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={2}>
          {error && <Alert severity="error">{error}</Alert>}
          {info && <Alert severity="success" onClose={() => setInfo(null)}>{info}</Alert>}

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems="flex-start">
            <TextField
              size="small"
              label="Display name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              disabled={!canWrite || busy}
              fullWidth
            />
            <TextField
              size="small"
              label="Timezone (IANA)"
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              disabled={!canWrite || busy}
              fullWidth
              helperText={`Examples: ${COMMON_TIMEZONES.slice(0, 3).join(', ')}`}
              inputProps={{ list: 'epic281-timezone-options' }}
            />
            <datalist id="epic281-timezone-options">
              {COMMON_TIMEZONES.map((tz) => (
                <option key={tz} value={tz} />
              ))}
            </datalist>
            <Button
              variant="contained"
              onClick={saveSiteMeta}
              disabled={!canWrite || busy}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Save site
            </Button>
          </Stack>

          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Areas (flat)
          </Typography>
          <Stack spacing={1}>
            {areas.length === 0 ? (
              <Typography variant="body2" color="text.secondary">—</Typography>
            ) : (
              areas.map((area) => (
                <Stack
                  key={area.area_id}
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={1}
                  alignItems={{ md: 'center' }}
                >
                  <Chip
                    size="small"
                    label={area.area_id}
                    color={area.active === false ? 'default' : 'info'}
                    variant="outlined"
                  />
                  <TextField
                    size="small"
                    label="Display name"
                    value={renameDrafts[area.area_id] ?? ''}
                    onChange={(e) => setRenameDrafts((prev) => ({
                      ...prev,
                      [area.area_id]: e.target.value,
                    }))}
                    disabled={!canWrite || busy}
                    sx={{ flex: 1 }}
                  />
                  <Button size="small" onClick={() => renameArea(area.area_id)} disabled={!canWrite || busy}>
                    Rename
                  </Button>
                  <Button
                    size="small"
                    onClick={() => setAreaActive(area.area_id, area.active === false)}
                    disabled={!canWrite || busy}
                  >
                    {area.active === false ? 'Activate' : 'Deactivate'}
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    onClick={() => removeArea(area.area_id)}
                    disabled={!canWrite || busy}
                  >
                    Delete
                  </Button>
                </Stack>
              ))
            )}
          </Stack>

          {canWrite && (
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems="flex-start">
              <TextField
                size="small"
                label="New area_id"
                value={newAreaId}
                onChange={(e) => setNewAreaId(e.target.value)}
                disabled={busy}
              />
              <TextField
                size="small"
                label="Display name"
                value={newAreaName}
                onChange={(e) => setNewAreaName(e.target.value)}
                disabled={busy}
              />
              <Button variant="outlined" onClick={createArea} disabled={busy || !newAreaId.trim()}>
                Create area
              </Button>
            </Stack>
          )}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

export default SiteLocationSettingsPanel;
